import { Swiper } from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';

if (!customElements.get('product-media-slider')) {
  class ProductMediaSlider extends HTMLElement {
    constructor() {
      super();

      // Create elements
      this.mainSlider();

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        slideToVariant: (e) => this.slideToVariant(e),
      };

      // Listen for variant change and go to slide
      document.addEventListener('variant-change', this.reducer.slideToVariant);

      // Get upsell images and default images to hide on upsell selection indexes
      this.isCanvasOrPpp = false;
      if (this.querySelector('[data-upsell-image]')) {
        this.upsellImageIndexes = Array.from(this.querySelectorAll('[data-upsell-image]')).map(
          (image) => getNodeIndex(image)
        );
        this.defaultImagesToHideOnUpsellIndexes = Array.from(
          this.querySelectorAll('[data-default-image-to-hide-on-upsell]')
        ).map((image) => getNodeIndex(image));
      }
    }

    /*
     * Create main slider
     */
    mainSlider() {
      // Check if Swiper exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      // Get swiper element
      this.swiperMain = this.querySelector('[data-product-media-slider]');

      // Get initial slide and swiper index
      let initialSlideIndex = 0;
      const initialVariant = parseInt(this.swiperMain.dataset.productMediaSlider);

      if (initialVariant) {
        const initialSlide = this.matchVariantIdWithSlide(initialVariant);
        initialSlideIndex = initialSlide ? initialSlide : initialSlideIndex;
      }

      // Swiper options
      this.swiperMainOptions = {
        modules: [A11y, Navigation, Pagination],
        threshold: 10,
        direction: 'horizontal',
        initialSlide: initialSlideIndex,
        slidesPerView: 1,
        observer: true,
        observeParents: true,
        breakpoints: {
          768: {
            slidesPerView: 'auto',
            loopAddBlankSlides: true,
            slidesPerGroup: 2,
            spaceBetween: 4,
          },
        },
        navigation: {
          prevEl: this.querySelector('.product-media-slider__button-prev'),
          nextEl: this.querySelector('.product-media-slider__button-next'),
        },
        pagination: {
          el: this.querySelector('.product-media-slider__pagination'),
          type: 'bullets',
        },
      };

      // Check if we have extra options on the HTML
      if (this.swiperMain.dataset.options) {
        const options = JSON.parse(this.swiperMain.dataset.options);
        if (options) {
          this.swiperMainOptions = {
            ...this.swiperMainOptions,
            ...options,
          };
        }
      }

      // Create Main slider
      this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);

      // Rebuild Swiper in design mode
      if (Shopify.designMode && this.swiperMainInstance) {
        window.addEventListener('shopify:section:load', () => {
          this.swiperMainInstance.update();
        });
      }
    }

    /*
     * Slide to variant
     * @Param e {object}: event object with variant
     */
    slideToVariant(e) {
      if (e?.detail?.variant?.id) {
        const slideIndex = this.matchVariantIdWithSlide(e.detail.variant.id);
        if (typeof slideIndex == 'number') {
          this.goToSlide(slideIndex);
        }
      }
    }

    /*
     * Match variant id with slide
     * @param id {int}: variant id
     */
    matchVariantIdWithSlide(id) {
      if (!id || !this.swiperMain) {
        return false;
      }

      // Get slides
      const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

      // Match id with slides id's
      const slide = [...slides].find((slide) => {
        // Get variant id's attached to slide
        const slideVariants = JSON.parse(slide.dataset.productVariantImage);

        // Check if variant id match the slide variant id's
        if (slideVariants.includes(id)) {
          return slide;
        }
      });

      // return false if no slide found
      if (!slide) {
        return false;
      }

      // return matched slide index
      return getNodeIndex(slide);
    }

    /*
     * go to slide
     * @param slide {node} / {int}: slide node / index
     */
    goToSlide(slide) {
      if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) {
        return false;
      }

      // Get index, transform node to slide index or string to int
      const index = typeof slide == 'number' ? parseInt(slide) : getNodeIndex(slide);

      // go to slide
      this.swiperMainInstance.slideTo(index);
    }

    /*
     * showUpsellSlides
     * @description Show images related to Canvas frames or Photo Passe-Partout and hide images from the main product & Vice-versa
     * @param showUpsell {boolean}: Show upsell slides
     */
    showUpsellSlides(showUpsell) {
      if (!this.swiperMainInstance) return;

      this.isCanvasOrPpp = showUpsell;

      const indexesToShow = showUpsell
        ? this.upsellImageIndexes
        : this.defaultImagesToHideOnUpsellIndexes;
      const indexesToHide = showUpsell
        ? this.defaultImagesToHideOnUpsellIndexes
        : this.upsellImageIndexes;

      this.toggleSlides(indexesToShow, indexesToHide);
    }

    /*
     * toggleSlides
     * @description Show/hide slides
     * @param indexesToShow {array}: Array of slide indexes to show
     * @param indexesToHide {array}: Array of slide indexes to hide
     */
    toggleSlides(indexesToShow, indexesToHide) {
      const toggleSlide = (index, show) => {
        const slide = this.swiperMainInstance.slides[index];
        slide?.classList.toggle('hidden', !show);
      };

      indexesToShow.forEach((index) => toggleSlide(index, true));
      indexesToHide.forEach((index) => toggleSlide(index, false));

      this.swiperMainInstance.update();
    }
  }

  window.ProductMediaSlider = ProductMediaSlider;

  customElements.define('product-media-slider', ProductMediaSlider);
}
